'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetOpenPaymentsSuggestionsInfiniteQuery } from 'src/api/redux';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import {
  OpenPaymentItem,
  openPaymentNodeConfig,
  openPaymentModalConfig,
  transformOpenPayment,
} from './config';

interface OpenPaymentsComponentProps {
  componentId: string;
  initialOpenPayments?: OpenPaymentItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const OpenPaymentsComponent: React.FC<OpenPaymentsComponentProps> = ({
  componentId,
  initialOpenPayments = [],
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  // Handle legacy case where initialOpenPayments might contain string IDs
  const getInitialPayments = (): OpenPaymentItem[] => {
    if (initialOpenPayments.length === 0) return [];
    const first = initialOpenPayments[0];
    // If it's already an object with id and name, use it directly
    if (typeof first === 'object' && first !== null && 'id' in first) {
      return initialOpenPayments as OpenPaymentItem[];
    }
    // If it's just an ID string, create a placeholder
    if (typeof first === 'string') {
      return [{ id: first, displayLabel: first, name: first }];
    }
    return [];
  };

  const [openPayments, setOpenPayments] =
    useState<OpenPaymentItem[]>(getInitialPayments);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialOpenPayments.length === 0
  );

  // Fetch open payments suggestions
  const { data, isLoading } = useGetOpenPaymentsSuggestionsInfiniteQuery({
    q: debouncedSearch,
  });

  // Transform API results to standard format
  const searchResults: OpenPaymentItem[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results ?? []).map(transformOpenPayment);
  }, [data]);

  // Auto-open modal if starting with no open payments (new component)
  useEffect(() => {
    if (initialOpenPayments.length === 0) {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    const initial = getInitialPayments();
    if (initial.length > openPayments.length) {
      setOpenPayments(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenPayments.length]);

  const handleSave = useCallback(
    (newOpenPayments: OpenPaymentItem[], connector: ConnectorType) => {
      if (!newOpenPayments || newOpenPayments.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      const selectedPayment = newOpenPayments[0];
      setOpenPayments([selectedPayment]);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      // OpenPayments always use 'ANY' connector (single selection), but we accept the param for type compatibility
      onUpdate(componentId, {
        type: 'open_payments',
        value: selectedPayment.id,
        ui_metadata: [selectedPayment], // Store full object for hydration-free loading
        connector: connector || 'ANY',
      });

      setShowModal(false);
    },
    [componentId, onUpdate, onRemove]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && openPayments.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, openPayments.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  return (
    <span data-testid="open-payments-component" className="inline">
      {openPayments.length > 0 ? (
        <GenericNodeComponent
          config={openPaymentNodeConfig}
          items={openPayments}
          connector="ANY"
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-full cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => setShowModal(true)}
          data-testid="open-payments-select-trigger"
          aria-label="Select open payments company"
        >
          <span className="text-amber-600 text-xs font-medium">
            Select company...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={openPaymentModalConfig}
        isOpen={showModal}
        selectedItems={openPayments}
        connector="ANY"
        isEditing={openPayments.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </span>
  );
};

export default OpenPaymentsComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderOpenPaymentsComponent = (
  container: HTMLElement,
  props: OpenPaymentsComponentProps & {
    initialOpenPayments?: OpenPaymentItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <OpenPaymentsComponent {...props} />
    </Provider>
  );
  return root;
};
